import { State, Action, StateContext } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import * as formAction from './toastr.action';
import { ToastrType } from '@enums/toastr-type';
import { ToastrStateModel } from '@interfaces/toastr-state-model';
import { ActionType } from '@enums/action-type';
import { inject, Injectable } from '@angular/core';
import { IActionResult } from '@interfaces/action-result';

@State<ToastrStateModel>({
  name: 'toastr',
})
@Injectable()
export class ToastrState {
  private toastrService = inject(ToastrService);
  private translateService = inject(TranslateService);

  @Action(formAction.ShowToastr)
  showToastr(
    _: StateContext<ToastrStateModel>,
    { payload, error }: formAction.ShowToastr
  ): void {
    const settings = payload.settings;
    const type = ToastrType[payload.type || 3].toLocaleLowerCase();
    const action = ActionType[payload.action || 0].toLocaleLowerCase();
    const actionTitle = this.translateService.instant(`alert.action.${action}.title`);

    const actionMessage = this.translateService.instant(
      `alert.action.${action}.message`
    );

    const component = this.translateService.instant(payload.component);

    const title = this.translateService.instant(`alert.message.${type}.title`, {
      action: actionTitle,
      component,
    });

    let message = '';

    if (error && !error.success) {
      message = this.constructMessage(payload.text, error);
    } else {
      message = this.translateService.instant(`alert.message.${type}.message`, {
        action: actionMessage,
        component,
        text: payload.text,
      });
    }

    switch (payload.type) {
      case ToastrType.error:
        this.toastrService.error(message, title, settings);
        break;
      case ToastrType.success:
        this.toastrService.success(message, title, settings);
        break;
      case ToastrType.warning:
        this.toastrService.warning(message, title, settings);
        break;
      default:
        this.toastrService.info(message, title, settings);
        break;
    }
  }

  @Action(formAction.ShowSimpleToastr)
  showSimpleToastr(
    _: StateContext<ToastrStateModel>,
    { payload }: formAction.ShowSimpleToastr
  ): void {
    const title = this.translateService.instant(payload.title);
    const message = this.translateService.instant(payload.message);

    switch (payload.type) {
      case ToastrType.error:
        this.toastrService.error(message, title, payload.settings);
        break;
      case ToastrType.success:
        this.toastrService.success(message, title, payload.settings);
        break;
      case ToastrType.warning:
        this.toastrService.warning(message, title, payload.settings);
        break;
      default:
        this.toastrService.info(message, title, payload.settings);
        break;
    }
  }

  private constructMessage(name: string, error: IActionResult<void>): string {
    let message = name + '<br />';

    error.failureReasons.forEach(x => {
      message += this.translateService.instant(x.reason) + '<br />';

      x.data.forEach(d => {
        message += d.name + '<br />';
      });
    });

    return message;
  }
}
